// TwoFactorForm.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input, Button, Typography, Alert, Space } from 'antd';
import { KeyOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../../../constants/config';
import useSound from 'use-sound';

const { Title, Text } = Typography;

// Styled Components
const StyledInput = styled(Input).attrs<{ 'data-mode': 'dark' | 'light' }>(props => ({
  'data-mode': props['data-mode'],
}))`
  background-color: ${(props) => props['data-mode'] === 'dark' 
    ? 'rgba(31, 31, 31, 0.7)' 
    : 'rgba(245, 245, 245, 0.7)'};
  border: 1px solid ${(props) => props['data-mode'] === 'dark' ? '#333' : '#e1e1e1'};
  height: 50px;
  border-radius: 8px;
  color: ${(props) => props['data-mode'] === 'dark' ? '#fff' : '#333'};
  font-size: 20px;
  text-align: center;
  letter-spacing: 8px;

  .ant-input {
    background-color: transparent;
    color: ${(props) => props['data-mode'] === 'dark' ? '#fff' : '#333'};
    text-align: center;
    letter-spacing: 8px;
    font-size: 20px;
  }

  .ant-input-prefix {
    margin-right: 12px;
    color: #ac4949;
  }
`;

const VerifyButton = styled(motion(Button))`
  height: 50px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  background: #ac4949;
  border: none;
  box-shadow: 0 4px 12px rgba(172, 73, 73, 0.3);
  width: 49%;

  &:hover {
    background: #bc5959;
  }
`;

const CancelButton = styled(Button)`
  height: 50px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  width: 49%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const StyledAlert = styled(Alert).attrs<{ 'data-mode': 'dark' | 'light' }>(props => ({
  'data-mode': props['data-mode'],
}))`
  background-color: ${(props) => props['data-mode'] === 'dark' 
    ? 'rgba(68, 20, 20, 0.8)' 
    : 'rgba(255, 235, 235, 0.8)'};
  border: 1px solid #ac4949;
  border-radius: 8px;
  
  .ant-alert-message {
    color: ${(props) => props['data-mode'] === 'dark' ? '#fff' : '#ac4949'};
  }
  
  .ant-alert-description {
    color: ${(props) => props['data-mode'] === 'dark' 
      ? 'rgba(255, 255, 255, 0.8)' 
      : 'rgba(172, 73, 73, 0.8)'};
  }
  
  .anticon {
    color: ${(props) => props['data-mode'] === 'dark' ? '#e88a8a' : '#ac4949'};
  }
`;

const CodeInput = ({ value, onChange, onPressEnter, theme, isLoading }) => {
  return (
    <StyledInput
      size="large"
      maxLength={6}
      value={value}
      onChange={onChange}
      onPressEnter={onPressEnter}
      placeholder="000000"
      prefix={<KeyOutlined />}
      data-mode={theme}
      disabled={isLoading}
      autoFocus
    />
  );
};

interface TwoFactorFormProps {
  userId: string;
  username: string;
  theme: 'dark' | 'light';
  onSuccess: (response: any) => void;
  onCancel: () => void;
}

export const TwoFactorForm: React.FC<TwoFactorFormProps> = ({ 
  userId, 
  username,
  theme, 
  onSuccess, 
  onCancel
}) => {
  const [code, setCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(300); // 5 minutes countdown
  const [playSuccess] = useSound('/sounds/success.mp3');
  const autoSubmitRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => { isLoadingRef.current = isLoading; }, [isLoading]);

  const autoFillAndSubmit = useCallback((digits: string) => {
    if (autoSubmitRef.current || isLoadingRef.current) return;
    autoSubmitRef.current = true;
    setCode(digits);
    setError(null);
  }, []);

  // When code reaches 6 digits via auto-fill, submit automatically
  useEffect(() => {
    if (code.length === 6 && autoSubmitRef.current && !isLoading) {
      autoSubmitRef.current = false;
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Clipboard observer: detect copied 6-digit codes
  useEffect(() => {
    const extractCode = (text: string): string | null => {
      const cleaned = text.replace(/\s/g, '');
      if (/^\d{6}$/.test(cleaned)) return cleaned;
      const match = cleaned.match(/\b(\d{6})\b/);
      return match ? match[1] : null;
    };

    const tryReadClipboard = async () => {
      try {
        if (!navigator.clipboard?.readText) return;
        const text = await navigator.clipboard.readText();
        const digits = extractCode(text);
        if (digits) autoFillAndSubmit(digits);
      } catch {
        // Clipboard permission denied - ignore silently
      }
    };

    // On paste: intercept and auto-fill + submit
    const handlePaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text') || '';
      const digits = extractCode(text);
      if (digits) {
        e.preventDefault();
        autoFillAndSubmit(digits);
      }
    };

    // On window focus / tab visibility: try reading clipboard
    const handleFocus = () => tryReadClipboard();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') tryReadClipboard();
    };

    document.addEventListener('paste', handlePaste);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('paste', handlePaste);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [autoFillAndSubmit]);

  // Handle countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Automatically cancel when time runs out
          onCancel();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onCancel]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setCode(value);
    setError(null);
  };

  const handleSubmit = async () => {
    if (code.length !== 6) {
      setError('Por favor ingresa el código de 6 dígitos completo');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/verify-2fa`, {
        userId,
        code
      });

      if (response.data.success) {
        playSuccess();
        // Pass the authentication response to parent
        onSuccess(response.data);
      } else {
        setError('Verificación fallida. Por favor, intenta nuevamente.');
      }
    } catch (error) {
      console.error('[2FA] Error:', error);
      let errorMessage = 'Error en la verificación. Por favor, intenta nuevamente.';
      
      if (axios.isAxiosError(error) && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={3} style={{ 
          textAlign: 'center', 
          color: theme === 'dark' ? '#ffffff' : '#333333',
          marginBottom: '0.5rem' 
        }}>
          Verificación de dos factores
        </Title>
        
        <Text style={{ 
          display: 'block', 
          textAlign: 'center', 
          color: theme === 'dark' ? '#999999' : '#666666',
        }}>
          Hola <strong>{username}</strong>, por favor ingresa el código de verificación que 
          fue enviado a tu correo electrónico
        </Text>
        
        <Text style={{ 
          display: 'block', 
          textAlign: 'center', 
          fontSize: '14px',
          color: countdown < 60 ? '#ac4949' : (theme === 'dark' ? '#999999' : '#666666'),
        }}>
          Tiempo restante: {formatTime(countdown)}
        </Text>

        {error && (
          <StyledAlert
            message="Error de verificación"
            description={error}
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            closable
            onClose={() => setError(null)}
            data-mode={theme}
          />
        )}

        <CodeInput
          value={code}
          onChange={handleCodeChange}
          onPressEnter={handleSubmit}
          theme={theme}
          isLoading={isLoading}
        />

        <ButtonContainer>
          <CancelButton 
            onClick={onCancel} 
            disabled={isLoading}
          >
            Volver
          </CancelButton>
          
          <VerifyButton
            type="primary"
            onClick={handleSubmit}
            loading={isLoading}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            Verificar
          </VerifyButton>
        </ButtonContainer>
      </Space>
    </motion.div>
  );
};